import { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { UserPlus, User } from 'lucide-react';
import AssignmentList from './AssignmentList';
import CollapsibleAssignmentHeader from './CollapsibleAssignmentHeader';
import { totalsByName } from './assignmentUtils';
import { useAuth } from '../../contexts/AuthContext';
import DateField from '../DateField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TechAssignmentSection({ jobId, assignments, technicians, canModify, onReload, onEdit }) {
  const [form, setForm] = useState({ tech_id: '', date: '', hours: '', overtime_hours: '' });
  const [expanded, setExpanded] = useState(false);
  const { user } = useAuth();

  // Technicians can only see themselves in the dropdown
  const isTechnician = user?.role === 'technician';
  const availableTechs = isTechnician 
    ? technicians.filter(t => t.name?.toLowerCase().trim() === user?.name?.toLowerCase().trim())
    : technicians;
  
  // Get the current user's tech ID if they are a technician
  const currentUserTechId = isTechnician 
    ? technicians.find(t => t.name?.toLowerCase().trim() === user?.name?.toLowerCase().trim())?.id
    : null;
  
  // Technicians can assign themselves, managers/foremen can assign anyone
  const canAssign = canModify || (isTechnician && availableTechs.length > 0);

  const { grandTotal, grandOvertime, perName } = totalsByName(assignments, 'tech_name');

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.tech_id || !form.date) { toast.error('Select tech and date'); return; }
    try {
      const payload = { 
        job_id: jobId, 
        tech_id: form.tech_id,
        date: form.date,
        hours: form.hours ? parseFloat(form.hours) : null,
        overtime_hours: form.overtime_hours ? parseFloat(form.overtime_hours) : null,
      };
      await axios.post(`${API}/assignments`, payload);
      setForm({ tech_id: '', date: '', hours: '', overtime_hours: '' });
      onReload();
      toast.success('Assigned');
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleRemove = async (id) => {
    try { await axios.delete(`${API}/assignments/${id}`); onReload(); toast.success('Removed'); }
    catch (error) { toast.error('Failed'); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="tech-assignments-section">
      <CollapsibleAssignmentHeader
        title="Technician Assignments"
        icon={UserPlus}
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        count={assignments.length}
        totalHours={grandTotal}
        totalOvertime={grandOvertime}
        perName={perName}
        testid="tech-assignments"
      />
      {expanded && (
        <div className="mt-4">
          {canAssign && (
            <form onSubmit={handleAdd} className="mb-6 p-4 bg-slate-50 rounded-lg" data-testid="tech-assign-form">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                <div>
                  <Label className="text-xs text-slate-500">Tech{isTechnician && ' (You)'}</Label>
                  <Select value={form.tech_id} onValueChange={(v) => setForm({...form, tech_id: v})}>
                    <SelectTrigger data-testid="tech-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {availableTechs.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs text-slate-500">Date</Label><DateField value={form.date} onChange={(v) => setForm({...form, date: v})} data-testid="tech-date" /></div>
                <div>
                  <Label className="text-xs text-slate-500">Hours</Label>
                  <Input 
                    type="number" 
                    step="0.25" 
                    min="0" 
                    placeholder="e.g. 2.5" 
                    value={form.hours} 
                    onChange={(e) => setForm({...form, hours: e.target.value})} 
                    data-testid="tech-hours" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Overtime</Label>
                  <Input
                    type="number"
                    step="0.25"
                    min="0"
                    placeholder="opt."
                    value={form.overtime_hours}
                    onChange={(e) => setForm({...form, overtime_hours: e.target.value})}
                    data-testid="tech-overtime"
                  />
                </div>
                <Button type="submit" className="bg-slate-900" data-testid="tech-assign-btn"><UserPlus size={16} className="mr-1" />Assign</Button>
              </div>
            </form>
          )}
          <AssignmentList items={assignments} canModify={canModify} currentUserTechId={currentUserTechId} onEdit={(a) => onEdit(a, 'tech')} onRemove={handleRemove} icon={User} nameKey="tech_name" />
        </div>
      )}
    </div>
  );
}
